import { bunja } from 'bunja';
import { useBunja } from 'bunja/react';
import { atom, useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';

import { useAuth } from '@flipster/features/src/shared/auth/state';
import { useSpotHoldings } from '@flipster/features/src/shared/spot-holdings/state';
import { spotOrdersBunja } from '@flipster/r230522/original/spot-order';
import { Checkbox } from '@flipster/react/design-system/checkbox';
import { createSingleChoice } from '@flipster/react/design-system/chip';
import { Link } from '@flipster/ui';
import DocumentHistoryIcon from '@flipster/ui/assets/svgs/document-history.svg';
import segment from '@flipster/utils/segment';
import { PositionEvent } from '@flipster/utils/segment/events/position';

import { SymbolScope, tradeSymbolBunja } from '@web/app/[locale]/(web)/trade/_misc/symbol';

import styles from './positionSection.module.scss';

type PositionSectionTabType = 'purchased' | 'pending';
const Choice = createSingleChoice<'purchased' | 'pending'>();

const spotPositionCountBunja = bunja(() => {
  const { pendingPositionsAtom } = bunja.use(spotOrdersBunja);

  return atom((get) => {
    const pendingPositions = get(pendingPositionsAtom);
    const pendingCount = Object.values(pendingPositions || {}).reduce(
      (totalCount, positionBySymbol) => totalCount + positionBySymbol.length,
      0,
    );

    return { pendingCount };
  });
});
spotPositionCountBunja.debugLabel = 'spotPositionCountBunja';

const symbolSpotPositionCountBunja = bunja(() => {
  const { pendingPositionsAtom } = bunja.use(spotOrdersBunja);
  const tradeSymbol = bunja.use(SymbolScope);

  return atom((get) => {
    const pendingPositions = get(pendingPositionsAtom);
    if (!tradeSymbol) return { symbolPendingCount: 0 };
    const symbolPendingCount = pendingPositions?.[tradeSymbol]?.length ?? 0;
    return { symbolPendingCount };
  });
});
symbolSpotPositionCountBunja.debugLabel = 'symbolSpotPositionCountBunja';

interface Props {
  tab: PositionSectionTabType;
  onChangeTab: (tab: PositionSectionTabType) => void;
  showAllSymbol: boolean;
  toggleShowAllSymbol: () => void;
}

const PositionSectionHeader = ({ tab, onChangeTab, showAllSymbol, toggleShowAllSymbol }: Props) => {
  const t = useTranslations();
  const { isCookieLoggedIn } = useAuth();
  const transAssets = useTranslations('Assets.r250401');
  const transTrade = useTranslations('Trade.r250401');
  const { symbol, symbolInfoAtom } = useBunja(tradeSymbolBunja);
  const symbolInfo = useAtomValue(symbolInfoAtom);
  const spotPositionCountAtom = useBunja(spotPositionCountBunja);
  const symbolSpotPositionCountAtom = useBunja(symbolSpotPositionCountBunja, [SymbolScope.bind(symbol)]);
  const { pendingCount } = useAtomValue(spotPositionCountAtom);
  const { symbolPendingCount } = useAtomValue(symbolSpotPositionCountAtom);
  const { holdings } = useSpotHoldings();

  const symbolKey = symbolInfo?.baseCurrency;

  const purchasedPositions = holdings.filter(([key]) => {
    const isSpotSymbol = showAllSymbol ? true : symbolKey === key;
    return isSpotSymbol;
  });

  return (
    <header className={styles.header}>
      <div className={styles.headerTitle}>
        <Choice value={tab} onChange={onChangeTab}>
          <Choice.Chip value='purchased' label={transAssets('Holdings')} badge={purchasedPositions.length} />
          <Choice.Chip
            value='pending'
            label={t('Assets.r251008.Orders')}
            badge={showAllSymbol ? pendingCount : symbolPendingCount}
          />
        </Choice>
        <Link
          className={styles.historyLink}
          shape='link2'
          size='medium'
          href='/history/spot/pnl'
          opener
          prefetch={isCookieLoggedIn}
        >
          <DocumentHistoryIcon />
        </Link>
      </div>
      <div className={styles.headerActions}>
        <Checkbox
          checked={showAllSymbol}
          onChange={() => {
            toggleShowAllSymbol();
            segment.track(PositionEvent.YourPositionsShowAllClick, {
              symbol,
              trade_type: 'spot',
              order_status: tab === 'purchased' ? 'Purchased' : 'Pending',
              is_checked: !showAllSymbol,
            });
          }}
          css={{ textStyle: 'Text/M/Regular', color: 'content.default.level-5' }}
        >
          {transTrade('Show_all_trading_pairs')}
        </Checkbox>
      </div>
    </header>
  );
};

export default PositionSectionHeader;
